import { X, Copy, Check, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import Editor from '@monaco-editor/react';

interface IngressYAMLModalProps {
  isOpen: boolean;
  onClose: () => void;
  yamlContent: string;
  isLoading?: boolean;
  onRefresh?: () => void;
}

export default function IngressYAMLModal({
  isOpen,
  onClose,
  yamlContent,
  isLoading = false,
  onRefresh
}: IngressYAMLModalProps) {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(yamlContent);
    setCopied(true);
    toast.success('YAML copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brand-blue-100 rounded-lg">
                <svg className="w-5 h-5 text-brand-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Ingress YAML</h2>
                <p className="text-sm text-gray-500">Current GitLab ingress manifest</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {onRefresh && (
                <button
                  onClick={onRefresh}
                  disabled={isLoading}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  title="Refresh"
                >
                  <RefreshCw className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
              )}
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 px-3 py-2 bg-brand-blue-50 hover:bg-brand-blue-100 text-brand-blue-600 rounded-lg transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    <span className="text-sm font-medium">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span className="text-sm font-medium">Copy</span>
                  </>
                )}
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <RefreshCw className="w-8 h-8 text-brand-blue-600 animate-spin mx-auto mb-2" />
                  <p className="text-gray-500">Loading YAML...</p>
                </div>
              </div>
            ) : (
              <div className="h-[550px] border border-gray-300 rounded-lg overflow-hidden">
                <Editor
                  key={yamlContent}
                  height="100%"
                  defaultLanguage="yaml"
                  value={yamlContent}
                  theme="vs-dark"
                  options={{
                    readOnly: true,
                    minimap: { enabled: true },
                    scrollBeyondLastLine: true,
                    fontSize: 14,
                    lineNumbers: 'on',
                    padding: { top: 16, bottom: 16 },
                    renderWhitespace: 'selection',
                    folding: true,
                    showFoldingControls: 'always',
                    foldingStrategy: 'indentation',
                    foldingHighlight: true,
                    unfoldOnClickAfterEndOfLine: true,
                    automaticLayout: true,
                    wordWrap: 'on',
                    glyphMargin: true,
                    lineDecorationsWidth: 10,
                    lineNumbersMinChars: 3,
                    scrollbar: {
                      vertical: 'visible',
                      horizontal: 'visible',
                      verticalScrollbarSize: 10,
                      horizontalScrollbarSize: 10,
                      useShadows: true,
                    },
                    cursorBlinking: 'smooth',
                    smoothScrolling: true,
                    bracketPairColorization: {
                      enabled: true,
                    },
                  }}
                  loading={
                    <div className="flex items-center justify-center h-full bg-gray-900">
                      <div className="text-center">
                        <div className="w-8 h-8 border-4 border-brand-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                        <p className="text-gray-400 text-sm">Loading editor...</p>
                      </div>
                    </div>
                  }
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">
              Lines: {yamlContent.split('\n').length} | Characters: {yamlContent.length}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
